import * as THREE from "three";
import { dom } from "../dom";
import { type FilterState, HIDDEN_GHOST_ID, state } from "../state";
import {
  disposeFragmentsModel,
  getActiveFragmentsModel,
  loadFragmentsBuffer,
  loadIfcAsFragments,
} from "../fragments/engine";
import { ifcLoader, ensureIfcLoaderReady, scene, camera, controls, ghostMaterial } from "../viewer/scene";
import { setStatus } from "../ui/status";
import { hideProgress, setProgress } from "../ui/progress";
import { t } from "../i18n";
import { rebuildMeshVisibility, removeTransparentSpaceMesh } from "./filters";
import { getIfcFileCacheSignature } from "./metaCache";
import { deleteFragFromCache, loadFragFromCache, saveFragToCache } from "./fragCache";
import { resetCompareState } from "./compare";
import { configureDeferredInit, resetDeferredInitState, startBackgroundInit } from "./deferredInit";
import { maybeUnzipIfczip } from "./ifczip";
import { disposeAnnotationSprites } from "../features/annotations";
import { clearAllMeasurements } from "../features/measure";
import { clearSelection, setSelectionCaption, updateVisibilityButtons } from "../features/selection";
import {
  applyClipping,
  captureModelBounds,
  resetClippingState,
  syncClippingUiFromState,
} from "../features/clipping";
import { clearColorRule } from "../features/colorRules";
import { exitPlanView, refreshPlanUi } from "../features/planView";
import { trackModelOpen } from "../analytics/events";
import { hidePublicWelcome, showPublicWelcome } from "../ui/publicWelcome";
import { resetXrayForNewModel } from "../features/xray";

// Эти материалы создаются движком Fragments
// быть утилизированы вместе с ней (в отличие от глобальных shared-материалов
// типа ghostMaterial/highlightMaterial — те реиспользуются для следующих моделей).
function disposeOwnedMaterial(material: unknown): void {
  if (!material) {
    return;
  }
  const m = material as THREE.Material;
  if (
    m === (ghostMaterial as unknown as THREE.Material) ||
    // Сравнения по имени глобальных материалов сцены — простое и надёжное.
    (m as any).__isSharedSceneMaterial === true
  ) {
    return;
  }
  m.dispose?.();
}

function disposeOwnedTexture(texture: unknown): void {
  if (!texture) {
    return;
  }
  (texture as THREE.Texture).dispose?.();
}

export function disposeModel(model: any): void {
  if (!model) {
    return;
  }

  clearSelection();
  exitPlanView({ restoreVisibility: false });
  clearColorRule();
  disposeAnnotationSprites();
  state.annotations.length = 0;
  try {
    ifcLoader.ifcManager.removeSubset(model.modelID, ghostMaterial, HIDDEN_GHOST_ID);
  } catch (_) {
    /* ignore */
  }
  removeTransparentSpaceMesh();
  void disposeFragmentsModel(state.fragmentsModelId);
  resetDeferredInitState();
  state.fragmentsModelId = null;
  state.geometryExpressIds = null;
  state.hiddenExpressIds.clear();
  state.transparentSpaceIds.clear();
  state.elementMeta.clear();
  state.storeyLevels = [];
  state.ifcTree = null;
  state.modelBounds = null;
  clearAllMeasurements();
  resetClippingState();
  updateVisibilityButtons();
  scene.remove(model);

  model.traverse((obj: any) => {
    if (obj.isMesh || obj.isLine || obj.isPoints) {
      const geom = obj.geometry as THREE.BufferGeometry | undefined;
      geom?.dispose?.();
      const mat = obj.material as THREE.Material | THREE.Material[] | undefined;
      if (Array.isArray(mat)) {
        for (const m of mat) {
          const anyMat = m as any;
          disposeOwnedTexture(anyMat?.map);
          disposeOwnedTexture(anyMat?.normalMap);
          disposeOwnedTexture(anyMat?.roughnessMap);
          disposeOwnedTexture(anyMat?.metalnessMap);
          disposeOwnedTexture(anyMat?.alphaMap);
          disposeOwnedMaterial(m);
        }
      } else if (mat) {
        const anyMat = mat as any;
        disposeOwnedTexture(anyMat?.map);
        disposeOwnedTexture(anyMat?.normalMap);
        disposeOwnedTexture(anyMat?.roughnessMap);
        disposeOwnedTexture(anyMat?.metalnessMap);
        disposeOwnedTexture(anyMat?.alphaMap);
        disposeOwnedMaterial(mat);
      }
    }
    if (obj.isSprite) {
      const sm = obj.material as THREE.SpriteMaterial | undefined;
      disposeOwnedTexture(sm?.map);
      sm?.dispose?.();
    }
  });

  // Сам контейнер тоже мог нести geometry/material поверх traverse.
  const rootGeom = (model as any).geometry as THREE.BufferGeometry | undefined;
  rootGeom?.dispose?.();
}

/**
 * BBox модели. У Fragments геометрия в GPU-буферах, поэтому Box3.setFromObject
 * даёт пустой результат — берём model.box, а setFromObject оставляем фолбэком.
 */
function getModelBoundingBox(object3d: THREE.Object3D): THREE.Box3 {
  const model = state.fragmentsModelId
    ? getActiveFragmentsModel(state.fragmentsModelId)
    : undefined;
  if (model) {
    try {
      const b = model.box;
      if (b && !b.isEmpty()) {
        return b.clone();
      }
    } catch {
      /* fallback below */
    }
  }
  return new THREE.Box3().setFromObject(object3d);
}

export function frameObject(object3d: THREE.Object3D): void {
  const box = getModelBoundingBox(object3d);
  if (box.isEmpty()) {
    return;
  }
  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());
  const maxSize = Math.max(size.x, size.y, size.z);
  const fitDistance = maxSize * 1.2;

  camera.near = Math.max(0.1, fitDistance / 200);
  camera.far = Math.max(2000, fitDistance * 25);
  camera.updateProjectionMatrix();

  camera.position.set(center.x + fitDistance, center.y + fitDistance * 0.7, center.z + fitDistance);
  controls.target.copy(center);
  controls.update();
}

export function updateModelInfo(mesh: any, file: File): void {
  const bbox = getModelBoundingBox(mesh);
  const size = bbox.getSize(new THREE.Vector3());
  let triangles = 0;
  if (mesh.geometry?.index) {
    triangles = mesh.geometry.index.count / 3;
  } else if (mesh.geometry?.attributes?.position) {
    triangles = mesh.geometry.attributes.position.count / 3;
  }

  const details = [
    `Имя файла: ${file.name}`,
    `Размер: ${(file.size / (1024 * 1024)).toFixed(2)} MB`,
    `ID модели: ${mesh.modelID ?? state.fragmentsModelId ?? "n/a"}`,
    `Элементов с геометрией: ${(state.geometryExpressIds?.length ?? 0).toLocaleString("ru-RU")}`,
    triangles > 0 ? `Треугольники: ${Math.round(triangles).toLocaleString("ru-RU")}` : "Рендер: Fragments",
    `Габариты X/Y/Z: ${size.x.toFixed(2)} / ${size.y.toFixed(2)} / ${size.z.toFixed(2)}`,
  ];

  dom.infoElement.textContent = details.join("\n");
}

/**
 * Сохраняет сконвертированные .frag-байты в IndexedDB-кэш (best-effort, в фоне).
 * Для .frag-входа кэшируем сами входные байты; для IFC берём
 * `model.getBuffer(false)` (сжатый бинарник Fragments).
 */
async function persistFragCache(
  model: { getBuffer?: (raw?: boolean) => Promise<ArrayBuffer> },
  signature: string,
  preBuffer: ArrayBuffer | null,
): Promise<void> {
  try {
    const bytes = preBuffer ?? (model.getBuffer ? await model.getBuffer(false) : null);
    if (bytes && bytes.byteLength > 0) {
      await saveFragToCache(signature, bytes);
    }
  } catch (error) {
    // Кэш — это оптимизация; его недоступность не должна влиять на загрузку.
    console.warn("Не удалось сохранить Fragments в кэш:", error);
  }
}

export async function handleIfcFile(
  file: File | undefined,
  filterState: FilterState = { storeyId: "", types: [], mark: "" },
): Promise<void> {
  if (!file) {
    return;
  }

  setStatus(`Загрузка: ${file.name}...`);
  setProgress(null, `Загрузка: ${file.name}…`);
  hidePublicWelcome();

  try {
    await ensureIfcLoaderReady();

    // Если на вход пришёл .ifczip — распаковываем первый .ifc внутри.
    const isZip = /\.ifczip$/i.test(file.name);
    if (isZip) {
      setStatus(`Распаковка IFCZIP: ${file.name}...`);
      setProgress(null, `Распаковка IFCZIP: ${file.name}…`);
    }
    const innerFile = await maybeUnzipIfczip(file);
    if (innerFile !== file) {
      setStatus(`Загрузка: ${innerFile.name} (распакован из ${file.name})...`);
    }

    const cacheSignature = getIfcFileCacheSignature(innerFile);
    const modelId = `${innerFile.name}::${innerFile.size}`;
    const isFrag = /\.frag$/i.test(innerFile.name);

    state.currentIfcFileKey = null;
    disposeModel(state.ifcModel);
    state.fragmentsModelId = null;

    const onProgress = (pct: number, label: string) => {
      setStatus(`${label} (${innerFile.name})`);
      setProgress(pct >= 0 ? pct : null, label);
    };

    let fragModel;
    // Кэшировать стоит только успешно сконвертированный из IFC результат.
    // Для .frag-входа байты и так компактны, но кэшируем их тоже, чтобы файл
    // из СОД не качался повторно.
    let cachedFragHit = false;
    let fragBytesForCache: ArrayBuffer | null = null;
    if (isFrag) {
      const buffer = await innerFile.arrayBuffer();
      fragModel = await loadFragmentsBuffer(buffer, modelId, onProgress);
      fragBytesForCache = buffer;
    } else {
      // Клиентский кэш Fragments: если этот файл уже конвертировали — грузим
      // готовые .frag-байты, минуя парсинг IFC (на больших моделях это миг).
      const cachedFrag = await loadFragFromCache(cacheSignature);
      if (cachedFrag) {
        try {
          onProgress(0, "Загрузка из кэша Fragments…");
          fragModel = await loadFragmentsBuffer(cachedFrag, modelId, onProgress);
          cachedFragHit = true;
        } catch (cacheError) {
          // Битый кэш — выбрасываем и конвертируем заново из IFC.
          console.warn("Кэш Fragments повреждён, пересоздаём:", cacheError);
          void deleteFragFromCache(cacheSignature);
        }
      }
      if (!fragModel) {
        const bytes = new Uint8Array(await innerFile.arrayBuffer());
        fragModel = await loadIfcAsFragments(bytes, modelId, onProgress);
      }
    }

    state.fragmentsModelId = modelId;
    const modelObject = fragModel.object as THREE.Object3D & { modelID: string };
    (modelObject as any).modelID = modelId;
    state.ifcModel = modelObject;
    refreshPlanUi();
    state.ifcModel.frustumCulled = true;
    if (!state.ifcModel.parent) {
      scene.add(state.ifcModel);
    }

    // Только элементы С ГЕОМЕТРИЕЙ. getLocalIds() возвращает ВСЕ сущности
    // модели (включая IFCPROJECT, IFCPROPERTYSET, IFCSIUNIT, пространственные
    // контейнеры и т.п.), из-за чего список типов в фильтре засорялся
    // неотображаемыми классами, а выбор такого типа скрывал всю видимую
    // геометрию («модель пропадает»). getItemsIdsWithGeometry() даёт только
    // реальные геометрические элементы.
    try {
      state.geometryExpressIds = await fragModel.getItemsIdsWithGeometry();
    } catch {
      state.geometryExpressIds = await fragModel.getLocalIds();
    }

    state.hiddenExpressIds.clear();
    state.selectedExpressIds.clear();
    clearAllMeasurements();
    updateVisibilityButtons();
    state.currentIfcFileKey = `${file.name}::${file.size}`;
    state.currentIfcFileName = file.name;
    state.currentIfcRevision = null;
    // resetCompareState вызывает updateCompareButtonsState, который читает
    // currentIfcFileName для строки «Текущая модель», поэтому имя выставляем до него.
    resetCompareState(false);

    const hasShareLink = Boolean(window.location.hash && window.location.hash.length > 1);
    configureDeferredInit({
      cacheSignature,
      innerFile,
      modelId,
      filterState,
      applyShareLink: hasShareLink,
    });

    resetXrayForNewModel();
    frameObject(state.ifcModel);
    captureModelBounds();
    syncClippingUiFromState();
    applyClipping();
    updateModelInfo(state.ifcModel, file);
    setSelectionCaption(null);

    const sourceLabel = cachedFragHit
      ? "Fragments (кэш)"
      : isFrag
        ? "Fragments"
        : "IFC→Fragments";
    setStatus(`Модель загружена (${sourceLabel}): ${file.name}. Метаданные — по запросу в панели.`);
    hideProgress();
    startBackgroundInit();
    trackModelOpen(state.pendingModelSource || "pc", {
      sizeBytes: file.size,
      presentationId: state.pendingModelSource === "presentation" ? state.pendingPresentationId || undefined : undefined,
    });
    state.pendingModelSource = null;
    state.pendingPresentationId = null;

    // Сохраняем .frag-байты в кэш (в фоне), чтобы следующее открытие того же
    // файла было мгновенным — без повторной конвертации/скачивания.
    if (!cachedFragHit) {
      void persistFragCache(fragModel, cacheSignature, fragBytesForCache);
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : "Неизвестная ошибка";
    setStatus(`Ошибка загрузки IFC: ${message}`, true);
    console.error(error);
    hideProgress();
    showPublicWelcome();
  }
}
